import type { TranslateOptions, Locale } from "vue-i18n";
import { computed } from "vue";
// eslint-disable-next-line no-restricted-imports
import { useI18n } from "vue-i18n";
import type ILocaleKeys from "@/i18n/locales/en.json";
import { DEFAULT_LOCALE } from "@/domain/constants";
import { useCookie, useHeadSafe } from "#imports";

/** TODO: move to a @chatfood/ts-toolbelt-ish package */
type SafeKey<T> = T extends string | number ? `${T}` : never;
type Paths<O, P = ""> = O extends object
  ? {
      [K in keyof O]:
        | (P extends ""
            ? O[K] extends object
              ? never
              : K
            : `${SafeKey<P>}.${SafeKey<K>}`)
        | Paths<O[K], P extends "" ? K : `${SafeKey<P>}.${SafeKey<K>}`>;
    }[keyof O]
  : never;

export type ILocaleKeypath = Paths<typeof ILocaleKeys>;

// Echoes every named param back as "{name}", so resolved messages keep their
// placeholders for consumers that interpolate on their own (e.g. SatSetup).
const keepPlaceholders = new Proxy(
  { placeholder: "" },
  { get: (_, key) => (typeof key === "string" ? `{${key}}` : undefined) },
);

export const useLocale = () => {
  const {
    t: vueT,
    te: vueTe,
    tm: vueTm,
    getLocaleMessage: vueGetLocaleMessage,
    locale: vueLocale,
    setLocale: vueSetLocale,
    setLocaleCookie,
    availableLocales: vueAvailableLocales,
  } = useI18n();

  const t = (
    path: ILocaleKeypath,
    params: Record<string, string | number> = {},
    options?: TranslateOptions,
  ) => {
    return vueT(path, params, options ?? {});
  };

  const hasTranslation = (path: string): path is ILocaleKeypath => {
    return vueTe(path) || vueTe(path, DEFAULT_LOCALE);
  };

  const extractTranslations = <T>(basePath: string): T => {
    const defaultLocaleBranch = (path: string): object =>
      path
        .split(".")
        .reduce<unknown>(
          (node, key) => (node as Record<string, unknown> | undefined)?.[key],
          vueGetLocaleMessage(DEFAULT_LOCALE),
        ) ?? {};

    const resolve = (path: string): unknown => {
      if (hasTranslation(path)) return vueT(path, keepPlaceholders);
      // tm() returns the current locale's branch as-is, so keys it lacks
      // only show up in the default locale's branch.
      const keys = new Set([
        ...Object.keys(vueTm(path)),
        ...Object.keys(defaultLocaleBranch(path)),
      ]);
      return Object.fromEntries(
        [...keys].map((key) => [key, resolve(`${path}.${key}`)]),
      );
    };

    return resolve(basePath) as T;
  };

  const setLocale = async (locale: Locale) => {
    useHeadSafe({
      htmlAttrs: {
        lang: locale,
        dir: locale.startsWith("ar") ? "rtl" : "ltr",
      },
    });

    return await vueSetLocale(locale as Locale);
  };

  const availableLocales = computed<Locale[]>(() => {
    return vueAvailableLocales as Locale[];
  });

  const locale = vueLocale;

  const isLocale = (lang: string): lang is Locale => {
    return (availableLocales.value as readonly string[]).includes(lang);
  };

  const setDefaultLanguage = (
    language: string,
    acceptedLanguages: string[],
  ): void => {
    const supportedLangs = [...acceptedLanguages, language];
    const cookieLang = useCookie("i18n_redirected").value;

    if (
      cookieLang &&
      isLocale(cookieLang) &&
      supportedLangs.includes(cookieLang)
    ) {
      setLocale(cookieLang);
      return;
    }

    if (isLocale(language) && supportedLangs.includes(language)) {
      setLocale(language);
      return;
    }

    setLocale("en");
  };

  return {
    t,
    hasTranslation,
    extractTranslations,
    locale,
    setLocale,
    setLocaleCookie,
    setDefaultLanguage,
    isLocale,
    availableLocales,
  };
};
